import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { httpGet, httpPost, httpPatch } from "../http";
import {
  ArrowLeftIcon,
  PlusIcon,
  CheckIcon,
  ShoppingCartIcon,
  PackageIcon,
  XIcon,
} from "lucide-react";
import { useToast } from "../components/Toast";

export type ShoppingListProductType = {
  id: string;
  productId: string;
  gtinCode: string | null;
  ncmCode: string;
  quantity: number;
  isBought: boolean;
};

export type ShoppingListDetailType = {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  products: ShoppingListProductType[];
};

type StateType = {
  loaded: boolean;
  list: ShoppingListDetailType | null;
  showAddProductPopup: boolean;
  newProductGtin: string;
  newProductQuantity: string;
  adding: boolean;
};

export const ShoppingListShow = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<StateType>({
    loaded: false,
    list: null,
    showAddProductPopup: false,
    newProductGtin: "",
    newProductQuantity: "1",
    adding: false,
  });

  const { showError } = useToast();

  const loadList = useCallback(async () => {
    if (!id) return;

    const result = await httpGet(`/api/v1/shopping-lists/${id}`);

    if (result.error) {
      if (result.error === "logged_out") {
        window.location.href = "/dash/login";
        return;
      }

      showError("Failed to load shopping list");
      navigate("/dash/shopping-lists");
      return;
    }

    setState((prev) => ({
      ...prev,
      loaded: true,
      list: result.body,
    }));
  }, [id, showError, navigate]);

  useEffect(() => {
    if (id) {
      loadList();
    }
  }, [id, loadList]);

  const handleAddProduct = async () => {
    if (!state.newProductGtin.trim()) {
      showError("Digite o código de barras de um produto");
      return;
    }

    const quantity = parseInt(state.newProductQuantity);
    if (isNaN(quantity) || quantity < 1) {
      showError("Insira uma quantidade válida");
      return;
    }

    setState((prev) => ({ ...prev, adding: true }));

    const result = await httpPost(`/api/v1/shopping-lists/${id}/products`, {
      gtinCode: state.newProductGtin.trim(),
      quantity,
    });

    setState((prev) => ({ ...prev, adding: false }));

    if (result.error) {
      showError("Falha ao adicionar produto na lista");
      return;
    }

    setState((prev) => ({
      ...prev,
      showAddProductPopup: false,
      newProductGtin: "",
      newProductQuantity: "1",
    }));

    loadList();
  };

  const handleToggleBought = async (product: ShoppingListProductType) => {
    const result = await httpPatch(
      `/api/v1/shopping-lists/${id}/products/${product.id}`,
      { isBought: !product.isBought },
    );

    if (result.error) {
      showError("Falha ao atualizar status do produto");
      return;
    }

    setState((prev) => ({
      ...prev,
      list: prev.list
        ? {
            ...prev.list,
            products: prev.list.products.map((p) =>
              p.id === product.id ? { ...p, isBought: !p.isBought } : p,
            ),
          }
        : null,
    }));
  };

  if (!state.loaded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!state.list) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-600">
            Lista de compras não encontrada
          </h2>
          <Link
            to="/dash/shopping-lists"
            className="text-indigo-600 hover:text-indigo-800 mt-4 inline-block"
          >
            Voltar
          </Link>
        </div>
      </div>
    );
  }

  const notBoughtProducts = state.list.products.filter((p) => !p.isBought);
  const boughtProducts = state.list.products.filter((p) => p.isBought);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center gap-4 mb-8">
          <Link
            to="/dash/shopping-lists"
            className="text-indigo-600 hover:text-indigo-800 transition-colors"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <ShoppingCartIcon className="h-8 w-8 text-indigo-600" />
            {state.list.name}
          </h1>
        </div>

        <div className="bg-white rounded-lg shadow-lg">
          <div className="p-6 border-b border-gray-200 flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Itens</h2>
              <p className="text-sm text-gray-500">
                {notBoughtProducts.length} ite
                {notBoughtProducts.length !== 1 ? "ns" : "m"} para comprar,{" "}
                {boughtProducts.length} comprado
                {boughtProducts.length !== 1 ? "s" : ""}
              </p>
            </div>
            <button
              onClick={() =>
                setState((prev) => ({ ...prev, showAddProductPopup: true }))
              }
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <PlusIcon className="h-5 w-5" />
              Adicionar Produto
            </button>
          </div>

          <div className="divide-y divide-gray-200">
            {notBoughtProducts.length > 0 && (
              <div className="p-6">
                <h3 className="font-medium text-gray-900 mb-4">
                  Não comprados
                </h3>
                <div className="space-y-3">
                  {notBoughtProducts.map((product) => (
                    <div
                      key={product.id}
                      className="flex items-center gap-4 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      <button
                        onClick={() => handleToggleBought(product)}
                        className="flex-shrink-0 w-6 h-6 rounded-full border-2 border-gray-300 hover:border-indigo-600 transition-colors flex items-center justify-center"
                      >
                        {product.isBought && (
                          <CheckIcon className="h-4 w-4 text-indigo-600" />
                        )}
                      </button>
                      <PackageIcon className="h-5 w-5 text-gray-400" />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">
                          {product.gtinCode || product.ncmCode}
                        </div>
                        <div className="text-sm text-gray-500">
                          Quantidade: {product.quantity}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {boughtProducts.length > 0 && (
              <div className="p-6">
                <h3 className="font-medium text-gray-900 mb-4">Comprados</h3>
                <div className="space-y-3">
                  {boughtProducts.map((product) => (
                    <div
                      key={product.id}
                      className="flex items-center gap-4 p-3 rounded-lg border border-green-200 bg-green-50"
                    >
                      <button
                        onClick={() => handleToggleBought(product)}
                        className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 border-2 border-green-500 transition-colors flex items-center justify-center"
                      >
                        <CheckIcon className="h-4 w-4 text-white" />
                      </button>
                      <PackageIcon className="h-5 w-5 text-green-600" />
                      <div className="flex-1">
                        <div className="font-medium text-gray-700 line-through">
                          {product.gtinCode || product.ncmCode}
                        </div>
                        <div className="text-sm text-gray-500">
                          Quantidade: {product.quantity}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {state.list.products.length === 0 && (
              <div className="p-8 text-center">
                <PackageIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-600 mb-2">
                  Sem produtos
                </h3>
                <p className="text-gray-500 mb-4">
                  Comece adicionando produtos à lista!
                </p>
                <button
                  onClick={() =>
                    setState((prev) => ({ ...prev, showAddProductPopup: true }))
                  }
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 mx-auto transition-colors"
                >
                  <PlusIcon className="h-5 w-5" />
                  Adicionar Primeiro Produto
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {state.showAddProductPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-[90vw]">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                Adicionar Produto
              </h2>
              <button
                onClick={() =>
                  setState((prev) => ({
                    ...prev,
                    showAddProductPopup: false,
                    newProductGtin: "",
                    newProductQuantity: "1",
                  }))
                }
                className="text-gray-400 hover:text-gray-600"
              >
                <XIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Código de Barras
                </label>
                <input
                  type="text"
                  placeholder="Enter GTIN code..."
                  value={state.newProductGtin}
                  onChange={(e) =>
                    setState((prev) => ({
                      ...prev,
                      newProductGtin: e.target.value,
                    }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantidade
                </label>
                <input
                  type="number"
                  min="1"
                  value={state.newProductQuantity}
                  onChange={(e) =>
                    setState((prev) => ({
                      ...prev,
                      newProductQuantity: e.target.value,
                    }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() =>
                  setState((prev) => ({
                    ...prev,
                    showAddProductPopup: false,
                    newProductGtin: "",
                    newProductQuantity: "1",
                  }))
                }
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                disabled={state.adding}
              >
                Cancelar
              </button>
              <button
                onClick={handleAddProduct}
                disabled={state.adding}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                {state.adding ? "Adicionando..." : "Adicionar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
